import { clsx } from "clsx";
import { nanoid } from "nanoid";
import { addIndex, filter, is, map, prop, remove } from "ramda";
import { type FC, type ReactElement, type ReactNode, useEffect, useState } from "react";
import {
  ActionButton,
  Badge,
  Description,
  FormError,
  HStack,
  Label,
  Spacer,
  SymbolIcon,
  TriggerButton,
  VStack,
} from "widgets";
import formStyle from "widgets/form.module.css";
import { useDataAttributes } from "widgets/hooks";

import type { ProjectFile } from "@/graphql.ts";
import {
  FilePickerControlledDialog,
  type FilePickerProps,
} from "@/modules/files/components/FilePickerControlledDialog.tsx";
import { FileTile } from "@/modules/files/components/FileTile";
import { type GetFilesTileInfoQuery, useGetFilesTileInfoLazyQuery } from "@/modules/files/graphql/files-queries.gql.ts";
import type { GetArrayElementType } from "@/types.js";

type FileTileInfo = GetArrayElementType<GetFilesTileInfoQuery["projectFilesById"]>;

export interface FilesSelectProps
  extends Pick<FilePickerProps, "singleSelection" | "selectableFileTypes" | "baseFilterHint" | "fileDefinitionTypes"> {
  label: string;
  description?: ReactNode;
  onSelect(selection: number[]): void;
  selection: number[];
  error?: Error;
  isDisabled?: boolean;
  isRequired?: boolean;
  extraAction?: ReactElement;
}

const mapIndexed = addIndex<NonNullable<FileTileInfo>>(map);

/**
 * File id may be 0, -1 (hidden) or -2 (deleted)
.
 * You can have multiple file for such ids, so we can't really rely on id to remove a file from the list
 */
export const FilesSelect: FC<FilesSelectProps> = (props) => {
  const [isOpen, setOpen] = useState(false);

  const [getFilesInfo, { loading }] = useGetFilesTileInfoLazyQuery();
  const [files, setFiles] = useState<NonNullable<FileTileInfo>[]>([]);

  useEffect(() => {
    if (is(Array, props.selection) && props.selection.length > 0) {
      const restrictedFileIds = props.selection.filter((id) => id < 0);
      const unrestrictedFileIds = props.selection.filter((id) => id > 0);
      void getFilesInfo({ variables: { fileIds: unrestrictedFileIds } }).then((data) => {
        const files = data.data?.projectFilesById ?? [];
        const restrictedFiles = restrictedFileIds.map(
          (id) => ({ id, hidden: id == -1, deleted: id == -2 }) as FileTileInfo,
        );
        const allFiles = files.concat(restrictedFiles);
        if (allFiles) {
          setFiles(filter((f): f is NonNullable<FileTileInfo> => f != null, allFiles));
        }
      });
    } else setFiles([]);
  }, [props.selection, getFilesInfo]);

  const dataAttributes = useDataAttributes(props);

  return (
    <>
      <div
        className={formStyle.input_layout}
        data-testid={`${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}files-select`}
      >
        {!!props.label && (
          <HStack className={formStyle.input_layout_column1} style={{ alignItems: "baseline" }}>
            <Label>{props.label}</Label>
            <Spacer />
            {!props.isRequired && <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />}
          </HStack>
        )}
        <Description
          data-testid="files-select-description"
          className={clsx(formStyle.input_layout_column1, {
            [formStyle.description_with_tag]: props.label != undefined && !props.isRequired,
          })}
        >
          {props.description}
        </Description>
        {!!props.error && <FormError data-testid="files-select-field-error">{props.error.message}</FormError>}
        <VStack className={formStyle.input_layout_column1}>
          {mapIndexed(
            (file, index) =>
              file ? (
                <HStack key={file.id > 0 ? file.id : nanoid()}>
                  <FileTile
                    file={{ ...file, project: null } as ProjectFile}
                    style={{ flex: 1, width: "100%" }}
                    noPageChangeActions
                  />
                  <TriggerButton
                    icon={<SymbolIcon icon="cancel" filled size={16} style={{ color: "var(--form-placeholder)" }} />}
                    label=""
                    aria-label="Delete"
                    onPress={() => {
                      // We need to go with index-based removal because we can multiple files with the same id: 0, -1 (hidden) or -2 (deleted).
                      const newSelection = remove(index, 1, files);
                      props.onSelect?.(map(prop("id"), newSelection));
                    }}
                    isDisabled={props.isDisabled}
                  />
                </HStack>
              ) : null,
            files,
          )}
        </VStack>
        <ActionButton
          label={
            props.singleSelection
              ? `Select ${props.selection.length === 0 ? "one" : "another"} file`
              : `Select ${props.selection.length === 0 ? "some" : "other"} files`
          }
          onPress={() => {
            setOpen(true);
          }}
          icon="file_open"
          isDisabled={props.isDisabled}
          isLoading={loading}
          className={formStyle.input_layout_column1}
        />
        {!!props.extraAction && <HStack className={formStyle.input_layout_column2}>{props.extraAction}</HStack>}
      </div>
      <FilePickerControlledDialog
        onValid={async (files) => {
          props.onSelect(map(prop("id"), files));
          setOpen(false);
        }}
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        title={props.label}
        defaultTab="filters"
        singleSelection={props.singleSelection}
        alreadySelectedFiles={props.selection}
        selectableFileTypes={props.selectableFileTypes}
        baseFilterHint={props.baseFilterHint}
        fileDefinitionTypes={props.fileDefinitionTypes}
      />
    </>
  );
};
